import { useEffect, useRef, useState } from "react";
import { ChevronDown, Printer } from "lucide-react";

export type ErpDocumentPrintMenuItem = {
  label: string;
  description?: string;
  disabled?: boolean;
  disabledReason?: string;
  onClick: () => void;
};

type ErpDocumentPrintMenuProps = {
  items: ErpDocumentPrintMenuItem[];
  label?: string;
  className?: string;
};

export default function ErpDocumentPrintMenu({ items, label = "列印", className = "" }: ErpDocumentPrintMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function closeOnOutside(event: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", closeOnOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return (
    <div className={`relative min-w-0 ${className}`.trim()} ref={rootRef}>
      <button
        aria-expanded={open}
        className="inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] transition hover:bg-[#f0f5f2] disabled:cursor-not-allowed disabled:opacity-50"
        disabled={items.length === 0}
        onClick={() => setOpen((value) => !value)}
        type="button"
      >
        <Printer size={17} />
        <span className="truncate">{label}</span>
        <ChevronDown className={`shrink-0 transition ${open ? "rotate-180" : ""}`} size={16} />
      </button>
      {open ? (
        <div className="absolute right-0 z-40 mt-2 w-[min(280px,calc(100vw-2rem))] overflow-hidden rounded-md border border-[#dfe6e1] bg-white p-1 shadow-[0_14px_36px_rgba(32,61,50,.16)]">
          {items.map((item) => (
            <button
              className="flex min-h-11 w-full items-start gap-2 rounded px-3 py-2 text-left text-sm font-bold text-[#315746] transition hover:bg-[#eef6f1] disabled:cursor-not-allowed disabled:opacity-45"
              disabled={item.disabled}
              key={item.label}
              onClick={() => {
                setOpen(false);
                item.onClick();
              }}
              title={item.disabled ? item.disabledReason : item.description}
              type="button"
            >
              <Printer className="mt-0.5 shrink-0" size={15} />
              <span className="min-w-0">
                <span className="block break-words">{item.label}</span>
                {item.description || item.disabledReason ? (
                  <span className="mt-0.5 block break-words text-xs font-semibold text-[#7b877f]">
                    {item.disabled ? item.disabledReason : item.description}
                  </span>
                ) : null}
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
